import type React from "react";
import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Avatar,
  Button,
  IconButton,
  Paper,
  Chip,
  Divider,
  CircularProgress,
  Tabs,
  Tab,
  TextField,
  Menu,
  MenuItem,
  TablePagination,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  FileDownloadOutlined as FileDownloadOutlinedIcon,
  Language as LanguageIcon,
  LinkedIn as LinkedInIcon,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  LocationOn as LocationOnIcon,
  Business as BusinessIcon,
  Add as AddIcon,
  Attachment as AttachmentIcon,
  MoreHoriz as MoreHorizIcon,
  ChevronRight as ChevronRightIcon,
  Twitter as TwitterIcon,
  Merge as MergeIcon,
  Description as DescriptionIcon,
  NoteAdd as NoteAddIcon,
} from "@mui/icons-material";

import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import { isRealSalutation } from "../../../constants/salutations";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { RootState } from "../../../store/Index";
import apiService from "../../../service/Api";
import type {
  IContact,
  IndividualContactDetails,
} from "../../../types/contact";
import UploadDocuments from "../../bookings/components/UploadDocuments";
import ContactMergeDialog from "./ContactMergeDialog";
import type { SimilarContact, MemorialComment } from "../../../types/contact";
import LinkToCompanyDialog from "./LinkToCompanyDialog";
import AddReferralDialog from "../../bookings/add-booking2/Components/AddReferralDialog";
import { downloadCSV, downloadVCard, vCardFilename } from "../utils/vCard";

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────
interface DocumentItem {
  id: number;
  name: string;
  description?: string;
  document: string;
  created_at?: string;
}

interface ContactResponse {
  id: number;
  full_name: string;
  email: string;
  role: string | null;
  phone: string;
  contact_image: string | null;
  branch_name: string;
}

interface ContactNoteResponse {
  id: number;
  contact: number;
  note: string;
  created_by: {
    first_name: string;
    last_name: string;
    email: string;
    profile_picture: string;
  };
  created_at: string;
  updated_at: string;
}

interface DisplayNote {
  id: number;
  author_name?: string;
  author_avatar?: string;
  created_at: string;
  content: string;
}

interface ExistingReferralCheck {
  exists: boolean;
  id?: number;
  status?: string;
  referral_name?: string;
  referral_surname?: string;
}

const CONTACT_TABS = [
  "Overview",
  "Company",
  "Bookings",
  "Memorial Activity",
  "Notes",
] as const;

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────
function getInitials(name: string) {
  if (!name?.trim()) return "?";
  return name
    .trim()
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function transformContactNote(n: ContactNoteResponse): DisplayNote {
  return {
    id: n.id,
    author_name:
      `${n.created_by?.first_name ?? ""} ${n.created_by?.last_name ?? ""}`.trim(),
    author_avatar: n.created_by?.profile_picture,
    created_at: n.created_at,
    content: n.note,
  };
}
function stringToColor(str: string) {
  const colors = [
    "#E11D48",
    "#7C3AED",
    "#2563EB",
    "#059669",
    "#D97706",
    "#DB2777",
    "#0891B2",
    "#65A30D",
  ];
  const idx = str.split("").reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return colors[idx % colors.length];
}

// Adapts the richer IndividualContactDetails shape used by this page into
// the flatter IContact shape the shared vCard/CSV export utilities expect
// (the same utilities the contacts list already uses), so a single
// contact's export stays byte-for-byte consistent with a bulk export.
function toExportableContact(
  contact: IndividualContactDetails,
  fullName: string,
  fullAddress: string,
): IContact {
  const company = contact.company_branch?.company;
  return {
    id: Number(contact.id) || 0,
    salutation: contact.salutation || null,
    contact_source: null,
    contact_type: "individual",
    name: fullName || null,
    first_name: contact.first_name || null,
    last_name: contact.last_name || null,
    email: contact.email || null,
    phone: contact.phone || null,
    address: fullAddress || null,
    city: contact.city || null,
    country: contact.country || null,
    website: contact.website || null,
    contactPerson: fullName || null,
    contactPersonAvatar: contact.contact_image || null,
    companyLogo: company?.company_logo || null,
    industry: null,
    taxNumber: null,
    jobTitle: contact.role || null,
    dateOfBirth: null,
    facebook_profile: contact.facebook_profile || null,
    phone_numbers: null,
    instagram_profile: contact.instagram_profile || null,
    twitter_profile: contact.twitter_profile || null,
    linkedin_profile: contact.linkedin_profile || null,
    notes: contact.notes || null,
    profile_image_file: contact.contact_image || null,
    category_type: null,
    company_id: company?.id ? String(company.id) : null,
    company_name: company?.name || null,
    company_email: null,
    created_at: new Date().toISOString(),
    created_by: null,
    updated_at: undefined,
    last_updated_by: null,
    type: "individual",
    contact_image: contact.contact_image || undefined,
    completeness_score: contact.completeness_score ?? 0,
  } as IContact;
}

function InfoBlock({
  label,
  value,
  type,
}: {
  label: string;
  value?: string | null;
  type?: "email" | "phone" | "url";
}) {
  const display = value || "—";
  const href =
    type === "email"
      ? `mailto:${value}`
      : type === "phone"
        ? `tel:${value}`
        : type === "url"
          ? value?.startsWith("http")
            ? value
            : `https://${value}`
          : undefined;

  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontSize: "0.68rem", display: "block", mb: 0.25 }}
      >
        {label}
      </Typography>
      {href && value ? (
        <Typography
          component="a"
          href={href}
          target={type !== "phone" ? "_blank" : undefined}
          rel="noopener noreferrer"
          variant="body2"
          fontWeight={500}
          sx={{
            fontSize: "0.82rem",
            color: "primary.main",
            textDecoration: "none",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {display}
        </Typography>
      ) : (
        <Typography
          variant="body2"
          fontWeight={500}
          sx={{ fontSize: "0.82rem" }}
        >
          {display}
        </Typography>
      )}
    </Box>
  );
}

function SocialCard({
  icon,
  label,
  url,
}: {
  icon: React.ReactNode;
  label: string;
  url: string;
}) {
  return (
    <Paper
      variant="outlined"
      component="a"
      href={url.startsWith("http") ? url : `https://${url}`}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 1.5,
        borderRadius: 2,
        textDecoration: "none",
        cursor: "pointer",
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      <Box sx={{ color: "text.secondary", display: "flex" }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" display="block">
          {label}
        </Typography>
        <Typography
          variant="caption"
          fontWeight={500}
          sx={{
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: 150,
            color: "primary.main",
          }}
        >
          {url}
        </Typography>
      </Box>
    </Paper>
  );
}

// ─────────────────────────────────────────────
// Note Post
// ─────────────────────────────────────────────
function NotePost({
  note,
  formatDate,
}: {
  note: {
    id: number;
    author_name?: string;
    author_avatar?: string;
    created_at: string;
    content: string;
  };
  formatDate: (d: string) => string;
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const initials = getInitials(note.author_name || "U");

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.75,
        borderRadius: 2,
        display: "flex",
        gap: 1.5,
      }}
    >
      <Avatar
        src={note.author_avatar}
        sx={{
          width: 32,
          height: 32,
          fontSize: 12,
          flexShrink: 0,
          bgcolor: note.author_avatar ? undefined : "primary.main",
        }}
      >
        {initials}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
          <Typography variant="body2" fontWeight={600}>
            {note.author_name || "Unknown"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatDate(note.created_at)}
          </Typography>
          <IconButton
            size="small"
            sx={{ ml: "auto" }}
            onClick={(e) => setAnchorEl(e.currentTarget)}
          >
            <MoreHorizIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
          >
            <MenuItem dense onClick={() => setAnchorEl(null)}>
              Edit
            </MenuItem>
            <MenuItem
              dense
              onClick={() => setAnchorEl(null)}
              sx={{ color: "error.main" }}
            >
              Delete
            </MenuItem>
          </Menu>
        </Box>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ lineHeight: 1.5 }}
        >
          {note.content}
        </Typography>
      </Box>
    </Paper>
  );
}
// ─────────────────────────────────────────────
// Tab: Overview
// ─────────────────────────────────────────────
function OverviewTab({
  contact,
  onEditClick,
}: {
  contact: IndividualContactDetails;
  onEditClick: () => void;
}) {
  const fullAddress = [
    contact.address_one,
    contact.address_two,
    contact.suburb,
    contact.city,
    contact.country,
  ]
    .filter(Boolean)
    .join(", ");

  const socials = [
    contact.linkedin_profile && {
      icon: <LinkedInIcon sx={{ fontSize: 18 }} />,
      label: "LinkedIn",
      url: contact.linkedin_profile,
    },
    contact.facebook_profile && {
      icon: <FacebookIcon sx={{ fontSize: 18 }} />,
      label: "Facebook",
      url: contact.facebook_profile,
    },
    contact.instagram_profile && {
      icon: <InstagramIcon sx={{ fontSize: 18 }} />,
      label: "Instagram",
      url: contact.instagram_profile,
    },
    contact.twitter_profile && {
      icon: <TwitterIcon sx={{ fontSize: 18 }} />,
      label: "X (Twitter)",
      url: contact.twitter_profile,
    },
    contact.website && {
      icon: <LanguageIcon sx={{ fontSize: 18 }} />,
      label: "Website",
      url: contact.website,
    },
  ].filter(Boolean) as { icon: React.ReactNode; label: string; url: string }[];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Contact Information */}
      <Paper variant="outlined" sx={{ p: 1, borderRadius: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="subtitle2" fontWeight={700}>
            Contact Information
          </Typography>
          <Button
            size="small"
            startIcon={<EditIcon sx={{ fontSize: 14 }} />}
            onClick={onEditClick}
            sx={{ textTransform: "none", fontSize: 12 }}
          >
            Edit
          </Button>
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <EmailIcon sx={{ fontSize: 16, color: "text.disabled", mt: 0.3 }} />
            <InfoBlock label="Email" value={contact.email} type="email" />
          </Box>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <PhoneIcon sx={{ fontSize: 16, color: "text.disabled", mt: 0.3 }} />
            <InfoBlock label="Phone" value={contact.phone} type="phone" />
          </Box>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <PhoneIcon sx={{ fontSize: 16, color: "text.disabled", mt: 0.3 }} />
            <InfoBlock label="Secondary Phone" value={undefined} />
          </Box>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <BusinessIcon
              sx={{ fontSize: 16, color: "text.disabled", mt: 0.3 }}
            />
            <InfoBlock label="Role" value={contact.role} />
          </Box>
          <Box
            sx={{
              gridColumn: "1 / -1",
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
            }}
          >
            <LocationOnIcon
              sx={{ fontSize: 16, color: "text.disabled", mt: 0.3 }}
            />
            <InfoBlock label="Address" value={fullAddress} />
          </Box>
        </Box>
      </Paper>

      {/* About */}
      {contact.notes && (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            About
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ lineHeight: 1.6 }}
          >
            {contact.notes}
          </Typography>
        </Paper>
      )}

      {/* Social Profile */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
          Social Profile
        </Typography>
        {socials.length > 0 ? (
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
            {socials.map((s) => (
              <SocialCard
                key={s.label}
                icon={s.icon}
                label={s.label}
                url={s.url}
              />
            ))}
          </Box>
        ) : (
          <Typography variant="caption" color="text.disabled">
            No social profiles added yet.
          </Typography>
        )}
      </Paper>
    </Box>
  );
}

// ─────────────────────────────────────────────
// Tab: Company
// ─────────────────────────────────────────────
function CompanyTab({
  contact,
  associatedContacts,
  loadingContacts,
  similarContacts,
  onMergeClick,
  canMerge,
}: {
  contact: IndividualContactDetails;
  associatedContacts: ContactResponse[];
  loadingContacts: boolean;
  similarContacts: SimilarContact[];
  onMergeClick: (id: number) => void;
  canMerge: boolean;
}) {
  const navigate = useNavigate();
  const company = contact.company_branch?.company;

  if (!company) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography variant="caption" color="text.disabled">
          This contact is not linked to any company.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Company Relationship */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
          Company Relationship
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 1.5 }}
        >
          This contact works for the following organisation:
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            p: 1.5,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            cursor: "pointer",
            "&:hover": { bgcolor: "action.hover" },
          }}
          onClick={() => navigate(`/companies/${company.id}`)}
        >
          <Avatar
            src={company.company_logo ?? undefined}
            sx={{ width: 36, height: 36, borderRadius: 1.5 }}
          >
            <BusinessIcon sx={{ fontSize: 18 }} />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600}>
              {company.name}
            </Typography>
          </Box>
          <ChevronRightIcon sx={{ fontSize: 16, color: "text.disabled" }} />
        </Box>
      </Paper>

      {/* Associated Contacts */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
          Associated Contacts at {company.name}
        </Typography>
        {loadingContacts ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : associatedContacts.length > 0 ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))",
              gap: 1.5,
              mt: 1.5,
            }}
          >
            {associatedContacts.map((c) => (
              <Paper
                key={c.id}
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  textAlign: "center",
                  cursor: "pointer",
                  "&:hover": { bgcolor: "action.hover" },
                }}
                onClick={() => navigate(`/contacts/individual/view/${c.id}`)}
              >
                <Avatar
                  src={c.contact_image ?? undefined}
                  sx={{ width: 44, height: 44, mx: "auto", mb: 1 }}
                >
                  {getInitials(c.full_name || "?")}
                </Avatar>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  display="block"
                  sx={{ fontSize: "0.72rem" }}
                >
                  {c.full_name || "—"}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ fontSize: "0.65rem" }}
                >
                  {c.role || c.branch_name || "—"}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 0.25,
                    mt: 0.75,
                  }}
                >
                  {c.phone && (
                    <IconButton
                      size="small"
                      component="a"
                      href={`https://wa.me/${c.phone.replace(/\D/g, "")}`}
                      target="_blank"
                      sx={{
                        color: "#25D366",
                        bgcolor: "rgba(37,211,102,0.08)",
                        width: 22,
                        height: 22,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <WhatsAppIcon sx={{ fontSize: 11 }} />
                    </IconButton>
                  )}
                  {c.email && (
                    <IconButton
                      size="small"
                      component="a"
                      href={`mailto:${c.email}`}
                      sx={{
                        color: "#1565C0",
                        bgcolor: "rgba(21,101,192,0.08)",
                        width: 22,
                        height: 22,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <EmailIcon sx={{ fontSize: 11 }} />
                    </IconButton>
                  )}
                  {c.phone && (
                    <IconButton
                      size="small"
                      component="a"
                      href={`tel:${c.phone}`}
                      sx={{
                        color: "#2E7D32",
                        bgcolor: "rgba(46,125,50,0.08)",
                        width: 22,
                        height: 22,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <PhoneIcon sx={{ fontSize: 11 }} />
                    </IconButton>
                  )}
                </Box>
              </Paper>
            ))}
          </Box>
        ) : (
          <Typography variant="caption" color="text.disabled">
            No other contacts at this company.
          </Typography>
        )}
      </Paper>

      {/* Potential Duplicates */}
      {similarContacts.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
            Potential Duplicate Detected
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {similarContacts.map((sc) => (
              <Box
                key={sc.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: "#fff8e1",
                  border: "1px solid #ffe082",
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: "#f59e0b",
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {sc.first_name} {sc.last_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    matched on phone & email · Muzukuru system contact, 2025
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => navigate(`/contacts/individual/view/${sc.id}`)}
                  sx={{
                    textTransform: "none",
                    fontSize: 11,
                    borderRadius: 1.5,
                    minWidth: "auto",
                    px: 1,
                  }}
                >
                  View Existing
                </Button>
                {canMerge && (
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => onMergeClick(sc.id)}
                    startIcon={<MergeIcon sx={{ fontSize: 13 }} />}
                    sx={{
                      textTransform: "none",
                      fontSize: 11,
                      borderRadius: 1.5,
                      minWidth: "auto",
                      px: 1,
                      bgcolor: "#8B4513",
                      "&:hover": { bgcolor: "#6d340f" },
                    }}
                  >
                    Merge
                  </Button>
                )}
                <Button
                  size="small"
                  sx={{
                    textTransform: "none",
                    fontSize: 11,
                    color: "text.secondary",
                    minWidth: "auto",
                    px: 1,
                  }}
                >
                  Dismiss
                </Button>
              </Box>
            ))}
          </Box>
        </Paper>
      )}
    </Box>
  );
}

// ─────────────────────────────────────────────
// Tab: Memorial Activity
// ─────────────────────────────────────────────
function MemorialActivityTab({
  comments,
  loading,
}: {
  comments: MemorialComment[];
  loading: boolean;
}) {
  const navigate = useNavigate();

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );

  const byStream = comments.reduce<Record<string, MemorialComment[]>>(
    (acc, c) => {
      const key = String(c.stream_id ?? "unknown");
      if (!acc[key]) acc[key] = [];
      acc[key].push(c);
      return acc;
    },
    {},
  );

  const streamEntries = Object.entries(byStream);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {streamEntries.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
            Memorial Pages Visited
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 1.5 }}
          >
            Pages this contact has engaged with
          </Typography>
          <Box
            sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}
          >
            {streamEntries.map(([streamId, streamComments]) => (
              <Paper
                key={streamId}
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  cursor: "pointer",
                  "&:hover": { bgcolor: "action.hover" },
                }}
                onClick={() => navigate(`/memory-table/view/${streamId}`)}
              >
                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.25 }}>
                  {streamComments[0].stream_title || `Memorial #${streamId}`}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {streamComments.length} comment
                  {streamComments.length !== 1 ? "s" : ""}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{ display: "block" }}
                >
                  {new Date(streamComments[0].created_at).toLocaleDateString(
                    "en-GB",
                    { day: "numeric", month: "short", year: "numeric" },
                  )}
                </Typography>
              </Paper>
            ))}
          </Box>
        </Paper>
      )}

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
          Comment History
        </Typography>
        {comments.length > 0 ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {comments.map((comment, i) => (
              <Box key={comment.id}>
                <Box sx={{ py: 1.5 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                    {comment.stream_title || `Memorial #${comment.stream_id}`}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: 1.5, fontStyle: "italic" }}
                  >
                    {comment.content}
                  </Typography>
                  {comment.sender_full_name && (
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      sx={{ display: "block", mt: 0.25 }}
                    >
                      — {comment.sender_full_name}
                    </Typography>
                  )}
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{ mt: 0.5, display: "block" }}
                  >
                    {new Date(comment.created_at).toLocaleDateString("en-GB", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </Typography>
                </Box>
                {i < comments.length - 1 && <Divider />}
              </Box>
            ))}
          </Box>
        ) : (
          <Typography variant="caption" color="text.disabled">
            No comment history yet.
          </Typography>
        )}
      </Paper>
    </Box>
  );
}

// ─────────────────────────────────────────────
// Tab: Notes
// ─────────────────────────────────────────────
function NotesTab({
  notes,
  contactName,
  onAddNote,
  formatDate,
}: {
  notes: DisplayNote[];
  contactName: string;
  onAddNote?: (content: string) => Promise<void>;
  formatDate: (d: string) => string;
}) {
  const [noteText, setNoteText] = useState("");
  const [saving, setSaving] = useState(false);

  const handlePost = async () => {
    if (!noteText.trim() || !onAddNote) return;
    setSaving(true);
    try {
      await onAddNote(noteText.trim());
      setNoteText("");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{ p: 2, borderRadius: 2, bgcolor: "background.paper" }}
    >
      {/* Compose */}
      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 2 }}>
        <TextField
          fullWidth
          multiline
          minRows={2}
          placeholder={`Add a note about ${contactName}`}
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          variant="standard"
          InputProps={{ disableUnderline: true, sx: { fontSize: "0.85rem" } }}
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            mt: 1,
            gap: 1,
          }}
        >
          <Button
            size="small"
            startIcon={<AttachmentIcon sx={{ fontSize: 14 }} />}
            sx={{
              textTransform: "none",
              fontSize: 12,
              color: "text.secondary",
            }}
          >
            Attach
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={!noteText.trim() || saving}
            onClick={handlePost}
            sx={{
              textTransform: "none",
              fontSize: 12,
              borderRadius: 2,
              bgcolor: "#8B4513",
              "&:hover": { bgcolor: "#6d340f" },
            }}
          >
            {saving ? "Posting…" : "Post Note"}
          </Button>
        </Box>
      </Paper>

      {/* Feed */}
      {notes.length > 0 ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {notes.map((note) => (
            <NotePost key={note.id} note={note} formatDate={formatDate} />
          ))}
        </Box>
      ) : (
        <Typography variant="caption" color="text.disabled">
          No notes yet. Add the first one above.
        </Typography>
      )}
    </Paper>
  );
}

// ─────────────────────────────────────────────
// Tab: Bookings
// ─────────────────────────────────────────────
interface ContactBookingResponse {
  id: number;
  status: string;
  title?: string | null;
  service?: string | null;
  package_item?: { name: string } | null;
  deceased?: Array<{ first_name?: string; surname?: string }>;
  services?: Array<{ date_of_service?: string }>;
}

function BookingsTab({ contactId }: { contactId: string }) {
  const navigate = useNavigate();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const [bookings, setBookings] = useState<ContactBookingResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"upcoming" | "past">("upcoming");
  const [page, setPage] = useState(0);
  const rowsPerPage = 5;

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const res = await apiService.get<{ results: ContactBookingResponse[] }>(
          `/api/bookings/booking/?contact_id=${contactId}&page_size=100`,
        );
        setBookings(res.data.results ?? []);
      } catch {
        setBookings([]);
      } finally {
        setLoading(false);
      }
    };
    fetchBookings();
  }, [contactId]);

  const now = new Date();

  const getBookingDate = (b: ContactBookingResponse): Date | null => {
    const raw = b.services?.[0]?.date_of_service;
    if (!raw) return null;
    const parsed = new Date(raw);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  const upcoming = bookings.filter((b) => {
    const date = getBookingDate(b);
    // No date yet (e.g. enquiry stage) still counts as upcoming
    return !date || date >= now;
  });
  const past = bookings.filter((b) => {
    const date = getBookingDate(b);
    return date !== null && date < now;
  });
  const display = activeTab === "upcoming" ? upcoming : past;

  // reset pagination whenever the filter changes
  useEffect(() => {
    setPage(0);
  }, [activeTab]);

  const statusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "confirmed":
        return { bgcolor: "#dcfce7", color: "#166534" };
      case "enquiry":
        return { bgcolor: "#fef9c3", color: "#854d0e" };
      case "cancelled":
        return { bgcolor: "#fee2e2", color: "#991b1b" };
      case "completed":
      case "fulfilled":
        return { bgcolor: "#e0f2fe", color: "#075985" };
      default:
        return { bgcolor: "#f3f4f6", color: "#374151" };
    }
  };

  const formatBookingDate = (date?: string | null) => {
    if (!date) return "—";
    return new Date(date).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const bookingName = (b: ContactBookingResponse) =>
    b.deceased?.[0]
      ? `${b.deceased[0].first_name ?? ""} ${b.deceased[0].surname ?? ""}`.trim()
      : b.title || `Booking #${b.id}`;

  const paginatedBookings = display.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
        {(["upcoming", "past"] as const).map((t) => (
          <Button
            key={t}
            size="small"
            variant={activeTab === t ? "contained" : "outlined"}
            onClick={() => setActiveTab(t)}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontSize: 12,
              bgcolor: activeTab === t ? "#8B4513" : undefined,
              borderColor: "#8B4513",
              color: activeTab === t ? "#fff" : "#8B4513",
              "&:hover": { bgcolor: activeTab === t ? "#6d340f" : undefined },
            }}
          >
            {t === "upcoming" ? "Upcoming" : "Past"} (
            {t === "upcoming" ? upcoming.length : past.length})
          </Button>
        ))}
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : display.length === 0 ? (
        <Typography variant="caption" color="text.disabled">
          No {activeTab} bookings.
        </Typography>
      ) : (
        <Box sx={{ width: "100%" }}>
          <Box sx={{ width: "100%", overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 13,
              }}
            >
              <thead>
                <tr>
                  {["Name", "Service", "Package", "Date", "Status"].map(
                    (col) => (
                      <th
                        key={col}
                        style={{
                          backgroundColor: reduxTheme.primary_colors?.[400],
                          color: "white",
                          padding: "10px 14px",
                          textAlign: "left",
                          fontWeight: 500,
                          fontSize: 13,
                          letterSpacing: 0.2,
                        }}
                      >
                        {col}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {paginatedBookings.map((booking, index) => (
                  <tr
                    key={booking.id}
                    style={{
                      backgroundColor:
                        index % 2 === 0
                          ? reduxTheme.primary_colors?.[900]
                          : reduxTheme.primary_colors?.[800],
                      cursor: "pointer",
                      transition: "background-color 0.15s",
                    }}
                    onClick={() => navigate(`/bookings/${booking.id}`)}
                    onMouseEnter={(e) => {
                      (
                        e.currentTarget as HTMLTableRowElement
                      ).style.backgroundColor =
                        reduxTheme.primary_colors?.[800];
                    }}
                    onMouseLeave={(e) => {
                      (
                        e.currentTarget as HTMLTableRowElement
                      ).style.backgroundColor =
                        index % 2 === 0
                          ? reduxTheme.primary_colors?.[900]
                          : reduxTheme.primary_colors?.[800];
                    }}
                  >
                    <td style={{ padding: "8px 14px" }}>
                      <Typography
                        variant="body2"
                        fontSize={13}
                        fontWeight={600}
                      >
                        {bookingName(booking)}
                      </Typography>
                    </td>
                    <td style={{ padding: "8px 14px" }}>
                      <Typography variant="body2" fontSize={13}>
                        {booking.service ?? "—"}
                      </Typography>
                    </td>
                    <td style={{ padding: "8px 14px" }}>
                      <Typography variant="body2" fontSize={13}>
                        {booking.package_item?.name ?? "—"}
                      </Typography>
                    </td>
                    <td style={{ padding: "8px 14px" }}>
                      <Typography variant="body2" fontSize={13}>
                        {formatBookingDate(
                          booking.services?.[0]?.date_of_service,
                        )}
                      </Typography>
                    </td>
                    <td style={{ padding: "8px 14px" }}>
                      <Chip
                        label={booking.status}
                        size="small"
                        sx={{
                          fontSize: 10,
                          height: 18,
                          fontWeight: 700,
                          textTransform: "capitalize",
                          "& .MuiChip-label": { px: 1 },
                          ...statusColor(booking.status),
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>

          <TablePagination
            component="div"
            count={display.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[5]}
            onRowsPerPageChange={() => {}}
          />
        </Box>
      )}
    </Box>
  );
}

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────
const IndividualContactView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const reduxTheme = useSelector((state: RootState) => state.theme);

  const [contactData, setContactData] =
    useState<IndividualContactDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [associatedContacts, setAssociatedContacts] = useState<
    ContactResponse[]
  >([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [similarContacts, setSimilarContacts] = useState<SimilarContact[]>([]);
  const [memorialComments, setMemorialComments] = useState<MemorialComment[]>(
    [],
  );
  const [commentsLoading, setCommentsLoading] = useState(false);

  // Merge dialog
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [secondaryContactId, setSecondaryContactId] = useState<number | null>(
    null,
  );
  const [secondaryContact, setSecondaryContact] =
    useState<IndividualContactDetails | null>(null);
  const [secondaryLoading, setSecondaryLoading] = useState(false);
  const [merging, setMerging] = useState(false);
  const [notes, setNotes] = useState<DisplayNote[]>([]);
  // Document upload
  const [dialogDocumentOpen, setDialogDocumentOpen] = useState(false);
  const [documentName, setDocumentName] = useState("");
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [documentDescription, setDocumentDescription] = useState("");
  const [documentCategory, setDocumentCategory] = useState("");
  const [linkCompanyDialogOpen, setLinkCompanyDialogOpen] = useState(false);
  const [referralDialogOpen, setReferralDialogOpen] = useState(false);
  const [existingReferralPartner, setExistingReferralPartner] =
    useState<ExistingReferralCheck | null>(null);

  // Export menu (vCard / CSV) for this single contact
  const [exportAnchorEl, setExportAnchorEl] = useState<null | HTMLElement>(
    null,
  );

  const userPermissions = useSelector(
    (state: RootState) => state.auth.user?.permissions || [],
  );
  const canMerge = (userPermissions as string[]).includes(
    "contact.delete_contact",
  );

  const formatDate = (date: string) =>
    new Date(date).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });

  const fetchAll = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [contactRes, docsRes, simRes] = await Promise.all([
        apiService.get<IndividualContactDetails>(
          `/api/contacts/contact/${id}/`,
        ),
        apiService.get<{ results: DocumentItem[] }>(
          `/api/contacts/contact-documents/?contact=${id}`,
        ),
        apiService
          .get<{
            similar_contacts: SimilarContact[];
          }>(`/api/contacts/contact/${id}/similar-contacts/`)
          .catch(() => ({ data: { similar_contacts: [] } })),
      ]);
      setContactData(contactRes.data);
      try {
        const params = new URLSearchParams();
        if (contactRes.data.email) params.set("email", contactRes.data.email);
        if (contactRes.data.phone && contactRes.data.phone !== "N/A") {
          params.set("phone", contactRes.data.phone);
        }
        if (params.toString()) {
          const refCheck = await apiService.get<ExistingReferralCheck>(
            `/api/refferal-program/refferal-programs/check-existing/?${params.toString()}`,
          );
          setExistingReferralPartner(refCheck.data);
        } else {
          setExistingReferralPartner({ exists: false });
        }
      } catch {
        setExistingReferralPartner(null); // fail-open: don't block the quick action if the check errors
      }
      setDocuments(docsRes.data.results ?? []);
      setSimilarContacts(simRes.data.similar_contacts ?? []);

      try {
        const notesRes = await apiService.get<{
          results: ContactNoteResponse[];
        }>(`/api/contacts/contact-notes/?contact=${id}`);
        setNotes((notesRes.data.results ?? []).map(transformContactNote));
      } catch {
        setNotes([]);
      }
      const company = contactRes.data.company_branch?.company;
      if (company?.id) {
        setLoadingContacts(true);
        try {
          const compRes = await apiService.get<{ results: ContactResponse[] }>(
            `api/contacts/contact/by-company/?company_id=${company.id}`,
          );
          setAssociatedContacts(
            (compRes.data.results ?? []).filter((c) => c.id !== Number(id)),
          );
        } catch {
          setAssociatedContacts([]);
        } finally {
          setLoadingContacts(false);
        }
      }
    } catch (err) {
      console.error("Failed to fetch contact:", err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Memorial comments
  useEffect(() => {
    if (!id) return;
    setCommentsLoading(true);
    apiService
      .get<{ comments: MemorialComment[] }>(
        `/api/contacts/contact/${id}/memorial-comments/`,
      )
      .then((res) => setMemorialComments(res.data.comments ?? []))
      .catch(() => setMemorialComments([]))
      .finally(() => setCommentsLoading(false));
  }, [id]);

  const handleSaveDocument = async () => {
    if (!documentFile || !documentName || !id) {
      toast.error("Please provide all document details.");
      return;
    }
    try {
      const formData = new FormData();
      formData.append("contact", id);
      formData.append("name", documentName);
      formData.append("category", documentCategory);
      formData.append("description", documentDescription);
      formData.append("document", documentFile);
      const res = await apiService.post(
        "/api/contacts/contact-documents/",
        formData,
      );
      setDocuments((prev) => [...prev, res.data as DocumentItem]);
      toast.success("Document uploaded successfully!");
      setDialogDocumentOpen(false);
      setDocumentName("");
      setDocumentFile(null);
    } catch {
      toast.error("Failed to upload document.");
    }
  };

  const handleAddNote = async (content: string) => {
    if (!id) return;
    try {
      const res = await apiService.post<ContactNoteResponse>(
        `/api/contacts/contact-notes/`,
        { contact: Number(id), note: content },
      );
      setNotes((prev) => [transformContactNote(res.data), ...prev]);
      toast.success("Note added.");
    } catch {
      toast.error("Failed to add note.");
    }
  };
  const handleMergeClick = async (targetId: number) => {
    setSecondaryContactId(targetId);
    setMergeDialogOpen(true);
    setSecondaryLoading(true);
    try {
      const res = await apiService.get<IndividualContactDetails>(
        `/api/contacts/contact/${targetId}/`,
      );
      setSecondaryContact(res.data);
    } catch {
      setSecondaryContact(null);
    } finally {
      setSecondaryLoading(false);
    }
  };

  const handleMergeConfirm = async (
    fieldSelections: Record<string, "primary" | "secondary">,
  ) => {
    if (!contactData || !secondaryContactId) return;
    setMerging(true);
    try {
      const res = await apiService.post<IndividualContactDetails>(
        `/api/contacts/contact/${id}/merge/`,
        {
          secondary_contact_id: secondaryContactId,
          field_selections: fieldSelections,
        },
      );
      setContactData(res.data);
      setMergeDialogOpen(false);
      toast.success("Contacts merged successfully!");
      fetchAll();
    } catch {
      toast.error("Failed to merge contacts.");
    } finally {
      setMerging(false);
    }
  };

  if (loading)
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 300,
        }}
      >
        <CircularProgress />
      </Box>
    );

  if (!contactData) return null;

  const fullName =
    `${contactData.first_name || ""} ${contactData.last_name || ""}`.trim();
  const displayName = [
    isRealSalutation(contactData.salutation) ? contactData.salutation : null,
    fullName,
  ]
    .filter(Boolean)
    .join(" ");
  const phoneNumber = contactData.phone?.replace(/\s+/g, "") || "";
  const company = contactData.companies?.[0];

  const fullAddress = [
    contactData.address_one,
    contactData.address_two,
    contactData.suburb,
    contactData.city,
    contactData.country,
  ]
    .filter(Boolean)
    .join(", ");

  // Single-contact export — reuses the same vCard/CSV utilities the
  // contacts list uses, so the output format is identical either way.
  const exportableContact = toExportableContact(
    contactData,
    fullName,
    fullAddress,
  );

  const handleExportMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setExportAnchorEl(event.currentTarget);
  };
  const handleExportMenuClose = () => setExportAnchorEl(null);

  const handleExportVCard = () => {
    downloadVCard([exportableContact], vCardFilename(exportableContact));
    toast.success("Exported vCard");
    handleExportMenuClose();
  };

  const handleExportSingleContactCSV = () => {
    const filename =
      (fullName || "contact")
        .replace(/[^a-z0-9]+/gi, "-")
        .replace(/^-|-$/g, "") || "contact";
    downloadCSV([exportableContact], `${filename}.csv`);
    toast.success("Exported CSV");
    handleExportMenuClose();
  };

  const socialIconChips = [
    contactData.linkedin_profile && {
      icon: <LinkedInIcon sx={{ fontSize: 14 }} />,
      href: contactData.linkedin_profile,
      label: "LinkedIn",
    },
    contactData.website && {
      icon: <LanguageIcon sx={{ fontSize: 14 }} />,
      href: contactData.website,
      label: "Website",
    },
    contactData.instagram_profile && {
      icon: <InstagramIcon sx={{ fontSize: 14 }} />,
      href: contactData.instagram_profile,
      label: "Instagram",
    },
    contactData.facebook_profile && {
      icon: <FacebookIcon sx={{ fontSize: 14 }} />,
      href: contactData.facebook_profile,
      label: "Facebook",
    },
    contactData.twitter_profile && {
      icon: <TwitterIcon sx={{ fontSize: 14 }} />,
      href: contactData.twitter_profile,
      label: "Twitter",
    },
    contactData.email && {
      icon: <EmailIcon sx={{ fontSize: 14 }} />,
      href: `mailto:${contactData.email}`,
      label: "Email",
    },
    phoneNumber && {
      icon: <WhatsAppIcon sx={{ fontSize: 14 }} />,
      href: `https://wa.me/${phoneNumber.replace(/\D/g, "")}`,
      label: "WhatsApp",
    },
  ].filter(Boolean) as { icon: React.ReactNode; href: string; label: string }[];

  const tabContent: Record<number, React.ReactNode> = {
    0: (
      <OverviewTab
        contact={contactData}
        onEditClick={() => navigate(`/contacts/individual/edit/${id}`)}
      />
    ),
    1: (
      <CompanyTab
        contact={contactData}
        associatedContacts={associatedContacts}
        loadingContacts={loadingContacts}
        similarContacts={similarContacts}
        onMergeClick={handleMergeClick}
        canMerge={canMerge}
      />
    ),
    2: <BookingsTab contactId={id!} />,
    3: (
      <MemorialActivityTab
        comments={memorialComments}
        loading={commentsLoading}
      />
    ),
    4: (
      <NotesTab
        notes={notes}
        contactName={fullName}
        onAddNote={handleAddNote}
        formatDate={formatDate}
      />
    ),
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2 }}>
        {/* ── Row 1: Hero Header + Quick Actions (equal height) ── */}
        <Box sx={{ display: "flex", gap: 2, alignItems: "stretch" }}>
          {/* Hero Header */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                p: 3,
                height: "100%",
                display: "flex",
                alignItems: "center",
                gap: 2.5,
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                position: "relative",
              }}
            >
              <Avatar
                src={contactData.contact_image ?? undefined}
                sx={{
                  width: 72,
                  height: 72,
                  fontSize: 26,
                  fontWeight: 700,
                  flexShrink: 0,
                  bgcolor: contactData.contact_image
                    ? undefined
                    : stringToColor(fullName),
                }}
              >
                {getInitials(fullName)}
              </Avatar>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{ lineHeight: 1.2, mb: 0.75 }}
                >
                  {displayName || fullName}
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2.5,
                    flexWrap: "wrap",
                    mb: 1.25,
                  }}
                >
                  {contactData.role && (
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <BusinessIcon
                        sx={{ fontSize: 15, color: "text.secondary" }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {contactData.role}
                      </Typography>
                    </Box>
                  )}
                  {company && (
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <LocationOnIcon
                        sx={{ fontSize: 15, color: "text.secondary" }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {company.name}
                      </Typography>
                    </Box>
                  )}
                  {fullAddress && (
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <LocationOnIcon
                        sx={{ fontSize: 15, color: "text.secondary" }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {fullAddress}
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {socialIconChips.map((chip) => (
                    <Box
                      key={chip.label}
                      component="a"
                      href={chip.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.6,
                        px: 1.5,
                        py: 0.5,
                        borderRadius: "999px",
                        border: "1px solid",
                        borderColor: "divider",
                        textDecoration: "none",
                        color: "text.secondary",
                        fontSize: 12,
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                    >
                      {chip.icon}
                      {chip.label}
                    </Box>
                  ))}
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  gap: 0.5,
                  position: "absolute",
                  top: 16,
                  right: 16,
                }}
              >
                <IconButton
                  size="small"
                  onClick={handleExportMenuOpen}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    color: "text.secondary",
                  }}
                >
                  <FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
                <Menu
                  anchorEl={exportAnchorEl}
                  open={Boolean(exportAnchorEl)}
                  onClose={handleExportMenuClose}
                  PaperProps={{ sx: { boxShadow: 0 } }}
                >
                  <MenuItem dense onClick={handleExportVCard}>
                    Export vCard
                  </MenuItem>
                  <MenuItem dense onClick={handleExportSingleContactCSV}>
                    Export CSV
                  </MenuItem>
                </Menu>
                <IconButton
                  size="small"
                  onClick={() => navigate(`/contacts/individual/edit/${id}`)}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    color: "text.secondary",
                  }}
                >
                  <EditIcon sx={{ fontSize: 16 }} />
                </IconButton>
                <IconButton
                  size="small"
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    color: "error.main",
                  }}
                >
                  <DeleteIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>

              {contactData.completeness_score !== undefined && (
                <Box
                  sx={{
                    position: "relative",
                    width: 48,
                    height: 48,
                    flexShrink: 0,
                    mr: 5,
                  }}
                >
                  <CircularProgress
                    variant="determinate"
                    value={100}
                    size={48}
                    thickness={3.5}
                    sx={{ color: "action.hover", position: "absolute" }}
                  />
                  <CircularProgress
                    variant="determinate"
                    value={contactData.completeness_score}
                    size={48}
                    thickness={3.5}
                    sx={{
                      color:
                        (contactData.completeness_score ?? 0) >= 75
                          ? "#4CAF50"
                          : "#2196F3",
                      position: "absolute",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography sx={{ fontSize: 11, fontWeight: 700 }}>
                      {contactData.completeness_score}%
                    </Typography>
                  </Box>
                </Box>
              )}
            </Paper>
          </Box>

          {/* Quick Actions */}
          <Box sx={{ width: 220, flexShrink: 0 }}>
            <Paper
              variant="outlined"
              sx={{ p: 2, borderRadius: 2, height: "100%" }}
            >
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                Quick Actions
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                {[
                  {
                    label: existingReferralPartner?.exists
                      ? "Already a Referral Partner"
                      : "Make a Referral",
                    icon: <AddIcon sx={{ fontSize: 14 }} />,
                    onClick: existingReferralPartner?.exists
                      ? undefined
                      : () => setReferralDialogOpen(true),
                  },
                  {
                    label: "Create Proposal",
                    icon: <DescriptionIcon sx={{ fontSize: 14 }} />,
                    onClick: () => {},
                  },
                  {
                    label: "Link to Company",
                    icon: <BusinessIcon sx={{ fontSize: 14 }} />,
                    onClick: () => setLinkCompanyDialogOpen(true),
                  },
                  {
                    label: "Add Note",
                    icon: <NoteAddIcon sx={{ fontSize: 14 }} />,
                    onClick: () => setActiveTab(4),
                  },
                ].map((action) => (
                  <Box
                    key={action.label}
                    onClick={action.onClick}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      px: 1,
                      py: 0.75,
                      borderRadius: 1.5,
                      cursor: action.onClick ? "pointer" : "default",
                      opacity: action.onClick ? 1 : 0.5,
                      "&:hover": action.onClick
                        ? { bgcolor: "action.hover" }
                        : undefined,
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.25 }}
                    >
                      <Box
                        sx={{
                          width: 26,
                          height: 26,
                          borderRadius: "50%",
                          bgcolor: "action.hover",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "text.secondary",
                          flexShrink: 0,
                        }}
                      >
                        {action.icon}
                      </Box>
                      <Typography variant="caption" fontWeight={500}>
                        {action.label}
                      </Typography>
                    </Box>
                    <ChevronRightIcon
                      sx={{ fontSize: 14, color: "text.disabled" }}
                    />
                  </Box>
                ))}
              </Box>
            </Paper>
          </Box>
        </Box>

        {/* ── Row 2: Tabs+Content + (Linked Contacts / Documents / Notes) ── */}
        <Box sx={{ display: "flex", gap: 2, alignItems: "stretch" }}>
          {/* Tabs + Content */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                p: 2,
                height: "100%",
              }}
            >
              <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                variant="fullWidth"
                sx={{
                  minHeight: 36,
                  "& .MuiTab-root": {
                    minHeight: 36,
                    textTransform: "none",
                    fontSize: 13,
                    px: 2,
                  },
                }}
              >
                {CONTACT_TABS.map((label) => (
                  <Tab key={label} label={label} />
                ))}
              </Tabs>
              <Box sx={{ mt: 2 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor:
                      reduxTheme.primary_colors?.[800] || "background.paper",
                    minHeight: 200,
                  }}
                >
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={activeTab}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.2 }}
                    >
                      {tabContent[activeTab]}
                    </motion.div>
                  </AnimatePresence>
                </Paper>
              </Box>
            </Paper>
          </Box>

          {/* ── Right Sidebar ── */}
          <Box
            sx={{
              width: 220,
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {/* Linked Contacts */}
            <Paper
              variant="outlined"
              sx={{ p: 2, borderRadius: 2, textAlign: "center" }}
            >
              {associatedContacts.length > 0 ? (
                <>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      rowGap: 1.5,
                      columnGap: 2,
                      justifyItems: "center",
                      mb: 2,
                      maxHeight: 150,
                      overflowY: "auto",
                    }}
                  >
                    {associatedContacts.map((c, idx) => {
                      const name = c.full_name || "?";
                      const isPrimary = idx === 0;
                      return (
                        <Box
                          key={c.id}
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <Avatar
                            src={c.contact_image ?? undefined}
                            title={name}
                            sx={{
                              width: 48,
                              height: 48,
                              fontSize: 15,
                              fontWeight: 700,
                              cursor: "pointer",
                              bgcolor: c.contact_image
                                ? undefined
                                : stringToColor(name),
                            }}
                            onClick={() =>
                              navigate(`/contacts/individual/view/${c.id}`)
                            }
                          >
                            {getInitials(name)}
                          </Avatar>
                          {isPrimary && (
                            <Typography
                              sx={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: "error.main",
                                lineHeight: 1,
                              }}
                            >
                              Primary
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Box>

                  <Typography variant="subtitle2" fontWeight={700}>
                    Linked Contacts
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {associatedContacts.length} people
                  </Typography>
                </>
              ) : (
                <>
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ mb: 1 }}
                  >
                    Linked Contacts
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    No linked contacts.
                  </Typography>
                </>
              )}
            </Paper>

            {/* Documents */}
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1.5,
                }}
              >
                <Typography variant="subtitle2" fontWeight={700}>
                  Documents
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setDialogDocumentOpen(true)}
                >
                  <AddIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
              {documents.length > 0 ? (
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}
                >
                  {documents.map((doc) => (
                    <Box
                      key={doc.id}
                      component="a"
                      href={doc.document}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 1,
                        py: 0.75,
                        borderRadius: 1.5,
                        textDecoration: "none",
                        color: "inherit",
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: 1,
                          bgcolor: "#fee2e2",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 8,
                            fontWeight: 700,
                            color: "#dc2626",
                          }}
                        >
                          PDF
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="caption"
                          fontWeight={500}
                          sx={{
                            display: "block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {doc.name}
                        </Typography>
                      </Box>
                      <IconButton size="small" sx={{ flexShrink: 0 }}>
                        <AttachmentIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="caption" color="text.disabled">
                  No documents uploaded.
                </Typography>
              )}
            </Paper>

            {/* Notes sidebar */}
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, flex: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1.5,
                }}
              >
                <Typography variant="subtitle2" fontWeight={700}>
                  Notes
                </Typography>
                <IconButton size="small" onClick={() => setActiveTab(4)}>
                  <AddIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
              {notes.length > 0 ? (
                <Typography variant="caption" color="text.secondary">
                  {notes.length} note{notes.length !== 1 ? "s" : ""} added.{" "}
                  <Typography
                    component="span"
                    variant="caption"
                    color="primary.main"
                    sx={{ cursor: "pointer" }}
                    onClick={() => setActiveTab(4)}
                  >
                    View all
                  </Typography>
                </Typography>
              ) : (
                <Box
                  sx={{
                    minHeight: 60,
                    bgcolor: "action.hover",
                    borderRadius: 1,
                    p: 1,
                  }}
                />
              )}
            </Paper>
          </Box>
        </Box>
      </Box>

      {/* Document Upload Dialog */}
      <UploadDocuments
        open={dialogDocumentOpen}
        loading={loading}
        setDialogDocumentOpen={setDialogDocumentOpen}
        setDocumentCategory={setDocumentCategory}
        documentCategory={documentCategory}
        documentName={documentName}
        documentDescription={documentDescription}
        setDocumentDescription={setDocumentDescription}
        setDocumentName={setDocumentName}
        setDocumentFile={setDocumentFile}
        documentFile={documentFile}
        handleSaveDocument={handleSaveDocument}
      />

      {/* Merge Dialog */}
      {contactData && (
        <ContactMergeDialog
          open={mergeDialogOpen}
          onClose={() => {
            setMergeDialogOpen(false);
            setSecondaryContact(null);
            setSecondaryContactId(null);
          }}
          primaryContact={contactData}
          secondaryContact={secondaryContact}
          secondaryLoading={secondaryLoading}
          onMerge={handleMergeConfirm}
          merging={merging}
        />
      )}
      <LinkToCompanyDialog
        open={linkCompanyDialogOpen}
        onClose={() => setLinkCompanyDialogOpen(false)}
        contactId={Number(id)}
        currentCompanyBranch={contactData.company_branch}
        onSuccess={fetchAll}
      />
      <AddReferralDialog
        open={referralDialogOpen}
        onClose={() => setReferralDialogOpen(false)}
        onSaved={() => {
          toast.success("Referral partner created from this contact.");
          setExistingReferralPartner({ exists: true });
        }}
        initialValues={{
          referral_name: contactData.first_name || "",
          referral_surname: contactData.last_name || "",
          email: contactData.email || "",
          phone:
            contactData.phone && contactData.phone !== "N/A"
              ? contactData.phone
              : "+263",
        }}
      />
    </motion.div>
  );
};

export default IndividualContactView;
